import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getIdToken } from '@/lib/firebase';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Helper function to get auth headers
async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  if (token) {
    return { 
      'Authorization': `Bearer ${token}`,
    };
  }
  return {};
}

export async function apiRequest(
  url: string,
  method: string = "GET",
  data?: unknown | undefined,
): Promise<any> {
  const authHeaders = await getAuthHeaders();
  const config: RequestInit = {
    method,
    credentials: "include",
    headers: authHeaders,
  };

  // Handle FormData differently from JSON
  if (data instanceof FormData) {
    config.body = data;
    // Don't set Content-Type for FormData, let browser set it with boundary
  } else if (data) {
    config.headers = { 
      ...authHeaders,
      "Content-Type": "application/json" 
    };
    config.body = JSON.stringify(data);
  }

  const res = await fetch(url, config);
  await throwIfResNotOk(res);
  
  // Try to parse as JSON, fallback to text if not JSON
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return await res.json();
  } else {
    return await res.text();
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: authHeaders,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});